import { createBrowserRouter, Navigate, RouterProvider, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth-context';
import { FilterProvider } from './lib/filter-context';
import { RootIndex } from './routes/RootIndex';
import { SignupPage } from './routes/SignupPage';
import { LoginPage } from './routes/LoginPage';
import { OnboardingPage } from './routes/OnboardingPage';
import { SettingsPage } from './routes/SettingsPage';
import { MfaSetupPage } from './routes/MfaSetupPage';
import { MfaChallengePage } from './routes/MfaChallengePage';
import { MirrorPage } from './routes/MirrorPage';
import { DashboardFourPsPage } from './routes/DashboardFourPsPage';
import { ChatIaPage } from './routes/ChatIaPage';
import { SourcesPage } from './routes/SourcesPage';
import { BillingPage } from './routes/BillingPage';
import { RiskRadarPage } from './routes/RiskRadarPage';
import { InsightsPage } from './routes/InsightsPage';
import { AppLayout } from './components/AppLayout';
import { ProductMetricsPage } from './routes/internal/ProductMetricsPage';
// F2-031: Telas de analise de campanhas Meta Ads
import { AdsCampaignsListPage } from './routes/AdsCampaignsListPage';
import { AdsCampaignDetailPage } from './routes/AdsCampaignDetailPage';
import { AdsCampaignsComparePage } from './routes/AdsCampaignsComparePage';
// Area Digital: shell com 4 abas + sub-telas de KPI
import {
  DigitalPage,
  DigitalOverviewTab,
  DigitalInstagramTab,
  DigitalMetaAdsTab,
} from './routes/DigitalPage';
// F3-001: Relatorio narrativo
import { ReportsListPage } from './routes/ReportsListPage';
import { ReportDetailPage } from './routes/ReportDetailPage';

function RequireAuth(): JSX.Element {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

/**
 * Redirect de /ads/campaigns/:id → /digital/campanhas/:id
 * Necessario para preservar o parametro dinamico :id no redirect.
 */
function RedirectCampaignDetail(): JSX.Element {
  // useParams nao disponivel aqui diretamente; usamos window.location para extair o id.
  // Alternativa: importar useParams — mas isso exige mover para componente filho da rota.
  // Solucao limpa: inspecionar o pathname atual.
  const id = window.location.pathname.replace('/ads/campaigns/', '').split('?')[0];
  const search = window.location.search;
  return <Navigate to={`/digital/campanhas/${id}${search}`} replace />;
}

const router = createBrowserRouter([
  { path: '/', element: <RootIndex /> },
  { path: '/signup', element: <SignupPage /> },
  { path: '/login', element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      // Rotas de MFA: logado, mas fora do AppLayout/MfaGate para evitar loop
      // de redirecionamento.
      { path: '/mfa/setup', element: <MfaSetupPage /> },
      { path: '/mfa/challenge', element: <MfaChallengePage /> },
      // Onboarding: fullscreen isolado — sem nav-rail nem topbar (fora do AppLayout).
      // Conforme prototipo onboarding.html (D-01 — relatorio de fidelidade 2026-06-04).
      { path: '/onboarding', element: <OnboardingPage /> },
      {
        element: (
          <FilterProvider>
            <AppLayout />
          </FilterProvider>
        ),
        children: [
          { path: '/mirror', element: <MirrorPage /> },
          { path: '/dashboard', element: <DashboardFourPsPage /> },
          { path: '/chat', element: <ChatIaPage /> },
          { path: '/sources', element: <SourcesPage /> },
          { path: '/billing', element: <BillingPage /> },
          { path: '/radar', element: <RiskRadarPage /> },
          { path: '/insights', element: <InsightsPage /> },
          { path: '/settings', element: <SettingsPage /> },
          { path: '/internal/product-metrics', element: <ProductMetricsPage /> },
          // F3-001: Relatorio narrativo
          { path: '/reports', element: <ReportsListPage /> },
          { path: '/reports/:id', element: <ReportDetailPage /> },
          // F2-031: Analise de campanhas Meta Ads (rotas canonicas antigas — mantidas para
          // links existentes; redirect transparente para /digital/campanhas*)
          {
            path: '/ads/campaigns/compare',
            element: <Navigate to="/digital/campanhas/compare" replace />,
          },
          { path: '/ads/campaigns/:id', element: <RedirectCampaignDetail /> },
          { path: '/ads/campaigns', element: <Navigate to="/digital/campanhas" replace /> },
          // Area Digital — shell com 4 abas
          {
            path: '/digital',
            element: <DigitalPage />,
            children: [
              { index: true, element: <DigitalOverviewTab /> },
              { path: 'instagram', element: <DigitalInstagramTab /> },
              { path: 'meta-ads', element: <DigitalMetaAdsTab /> },
              // Aba campanhas: sub-rotas dentro do shell Digital
              // /compare antes de /:id (mesma ordem que backend)
              { path: 'campanhas/compare', element: <AdsCampaignsComparePage /> },
              { path: 'campanhas/:id', element: <AdsCampaignDetailPage /> },
              { path: 'campanhas', element: <AdsCampaignsListPage /> },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

export function App(): JSX.Element {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
